import React, { useState } from "react";
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";

// FC251 / #1325: Phase 1 is an optional entry point only. A later linked
// change will associate versioned Document Manager documents with the parent.
// No backend document or Full Change fields are written at this stage.
const PLANNING_ENTRY_STORAGE_PREFIX = "devtools.fullChange.planningDocumentation.v1.";

const getStorageKey = (fullChangeId) => {
  const id = String(fullChangeId || "").trim();
  return /^\d+$/.test(id) ? `${PLANNING_ENTRY_STORAGE_PREFIX}${id}` : "";
};

const readPlanningEnabled = (storageKey) => {
  if (!storageKey) return false;
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(storageKey) === "1";
  } catch (_error) {
    return false;
  }
};

const FullChangePlanningDocumentationEntry = ({ fullChangeId, children }) => {
  const storageKey = getStorageKey(fullChangeId);
  const [enabled, setEnabled] = useState(() => readPlanningEnabled(storageKey));
  const [storageUnavailable, setStorageUnavailable] = useState(false);

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    try {
      if (next) window.localStorage.setItem(storageKey, "1");
      else window.localStorage.removeItem(storageKey);
      setStorageUnavailable(false);
    } catch (_error) {
      // The section remains usable in this session if storage is blocked.
      setStorageUnavailable(true);
    }
  };

  if (!storageKey) return null;

  return (
    <Paper
      data-testid="full-change-planning-documentation-entry"
      variant="outlined"
      sx={{ p: 2, mb: 2, borderColor: enabled ? "primary.light" : "divider" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="h6">Planning Documentation</Typography>
            {enabled && <Chip data-testid="full-change-planning-enabled" size="small" color="success" label="Enabled" />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Optional design and workflow documentation for this Full Change.
          </Typography>
        </Box>
        <Button
          data-testid="full-change-planning-toggle"
          variant={enabled ? "outlined" : "contained"}
          onClick={toggleEnabled}
          sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "center" } }}
        >
          {enabled ? "Hide Planning Documentation" : "Add Planning Documentation"}
        </Button>
      </Stack>

      {enabled && (
        <Box data-testid="full-change-planning-empty-state" sx={{ mt: 1.5, minWidth: 0 }}>
          {children || <Alert severity="info">Planning Documentation is enabled. No viewer is available yet.</Alert>}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            This opt-in is saved in this browser for Full Change #{fullChangeId}. Hiding the section never deletes documents or changes the implementation queue.
          </Typography>
        </Box>
      )}
      {storageUnavailable && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Browser storage is unavailable. This selection may reset when the page reloads.
        </Alert>
      )}
    </Paper>
  );
};

export default FullChangePlanningDocumentationEntry;
